import { useState, useMemo } from 'react';
import { CheckCircle } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { EmptyState } from '../../components/common/EmptyState';
import { JOB_STATUSES, TH, TD } from './config';
import type { JobRow } from './hooks';

interface Props {
  jobs: JobRow[];
  onSelect: (job: JobRow) => void;
}

function fmt$(n: number): string { return `$${n.toLocaleString('en-CA', { minimumFractionDigits: 0 })}`; }

export function CompletedTab({ jobs, onSelect }: Props) {
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(25);

  const completed = useMemo(() => {
    let list = jobs.filter(j => j.status === 'completed' || j.status === 'cancelled');
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(j => j.title.toLowerCase().includes(q) || (j.client_name ?? '').toLowerCase().includes(q));
    }
    return list.sort((a, b) => (b.completed_at ?? b.created_at).localeCompare(a.completed_at ?? a.created_at));
  }, [jobs, search]);

  // Group by month
  const groups = useMemo(() => {
    const map = new Map<string, JobRow[]>();
    for (const j of completed.slice(0, visibleCount)) {
      const d = j.completed_at ?? j.created_at;
      const key = d.substring(0, 7); // YYYY-MM
      const arr = map.get(key) ?? [];
      arr.push(j);
      map.set(key, arr);
    }
    return Array.from(map.entries()).map(([month, rows]) => ({
      label: new Date(month + '-15').toLocaleDateString('en-CA', { month: 'long', year: 'numeric' }),
      rows,
    }));
  }, [completed, visibleCount]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search completed jobs..."
          style={{ padding: '7px 12px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit', outline: 'none', width: 240 }} />
        <span style={{ fontSize: 13, color: palette.g400 }}>{completed.length} job{completed.length !== 1 ? 's' : ''}</span>
      </div>

      {completed.length === 0 ? (
        <EmptyState icon={<CheckCircle size={22} />} title="No completed jobs yet" subtitle="Completed jobs will appear here." />
      ) : (
        <div>
          {groups.map(g => (
            <div key={g.label} style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: palette.g400, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '8px 0', borderBottom: `1px solid ${palette.g150}` }}>
                {g.label} ({g.rows.length})
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={TH}>Title</th><th style={TH}>Client</th><th style={TH}>Date</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Amount</th><th style={TH}>Status</th>
                </tr></thead>
                <tbody>
                  {g.rows.map(j => {
                    const st = JOB_STATUSES[j.status] ?? JOB_STATUSES.completed;
                    return (
                      <tr key={j.id} onClick={() => onSelect(j)} style={{ cursor: 'pointer', transition: 'background 150ms ease' }}
                        onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                        <td style={{ ...TD, fontWeight: 500 }}>{j.title}</td>
                        <td style={TD}>{j.client_name ?? '—'}</td>
                        <td style={TD}>{(j.completed_at ?? j.created_at).split('T')[0]}</td>
                        <td style={{ ...TD, textAlign: 'right' }}>{j.total_amount ? fmt$(j.total_amount) : '—'}</td>
                        <td style={TD}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: st.bg, color: st.fg }}>{st.label}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
          {visibleCount < completed.length && (
            <button onClick={() => setVisibleCount(v => v + 25)} style={{
              width: '100%', padding: '10px 0', borderRadius: 6, border: `1px solid ${palette.g200}`,
              background: palette.white, fontSize: 12.5, fontWeight: 500, color: palette.g600,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>Load more</button>
          )}
        </div>
      )}
    </div>
  );
}
